import React, { useMemo } from 'react';
import Select from 'react-select';
import Datepicker from 'react-datepicker';
import { useSelector } from 'react-redux';

import Styles from './ContentPlanner.module.scss';
import {
  getContentTypeFilterOptions,
  getStatusFilterOptions,
  optionsClassName,
} from 'helpers/optionsBriefsFilters';
import useProjectUsers from 'hooks/projects/useProjectUsers';
import useProjects from 'hooks/projects/useProjects';

const SelectStatusStyles = {
  option: (styles, { isSelected }) => {
    return {
      boxShadow: isSelected
        ? 'box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #F4EBFF;'
        : null,
      padding: '4px',
      whiteSpace: 'nowrap',
      '&:hover': {
        backgroundColor: 'lightgrey',
        cursor: 'pointer',
      },
    };
  },

  menu: (baseStyles) => {
    return {
      ...baseStyles,
      width: 'auto',
      minWidth: '100%',
      whiteSpace: 'nowrap',
    };
  },

  menuList: (baseStyles) => {
    return {
      ...baseStyles,
      whiteSpace: 'nowrap',
    };
  },

  control: (baseStyles) => {
    return {
      ...baseStyles,
      cursor: 'pointer',
      minHeight: '32px',
      fontSize: '13px',
      minWidth: '160px',
      width: 'auto',
    };
  },

  valueContainer: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '2px 8px',
    };
  },

  indicatorsContainer: (baseStyles) => {
    return {
      ...baseStyles,
      height: '32px',
    };
  },

  dropdownIndicator: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '4px',
    };
  },
};

function ContentFilter({
  filterBy,
  filterValue,
  handleFilters,
  index,
  isOptions,
  project,
  autoOpen = false,
  chipMode = false, // New prop for inline chip editing
}) {
  const { user } = useSelector((state) => state.auth);
  const { user_brands } = user || {};

  const { data: projectUsers } = useProjectUsers({ project });

  // Fetch all projects/campaigns for filtering
  const { data: projectsData } = useProjects({ searchTerm: '', enabled: true });

  const users = useMemo(() => {
    if (project) {
      return (projectUsers?.data?.project_users || []).map(({ user }) => user);
    } else {
      return [user];
    }
  }, [project, projectUsers, user]);

  const userOptions = useMemo(() => {
    if (!users?.length) {
      return [];
    }

    return users.map((user) => {
      return {
        value: user.id,
        label: (
          <div className={optionsClassName}>
            <input
              type="checkbox"
              className="select-options form-check-input "
              checked={
                Array.isArray(filterValue) &&
                filterValue.some(
                  (filterVal) => Number(filterVal.value) === Number(user.id),
                )
              }
            />
            <label>{user.name}</label>
          </div>
        ),
      };
    });
  }, [users, filterValue]);

  // Brand options
  const brandOptions = useMemo(() => {
    // user_brands is an object with { brands_count, brands_data }
    // brands_data is an array like [{ "1": "Brand Name" }, { "2": "Brand Name 2" }]
    const brandsData = user_brands?.brands_data || [];

    if (!brandsData.length) {
      return [];
    }

    return brandsData.map((brandObj) => {
      // Extract id and name from object structure { "id": { brand_name: "name", ... } }
      const brandId = Object.keys(brandObj)[0];
      const brandData = Object.values(brandObj)[0];
      const brandName = brandData?.brand_name || brandData || 'Unknown';

      return {
        value: Number(brandId),
        label: (
          <div className={optionsClassName}>
            <input
              type="checkbox"
              className="select-options form-check-input "
              checked={
                Array.isArray(filterValue) &&
                filterValue.some(
                  (filterVal) => Number(filterVal.value) === Number(brandId),
                )
              }
            />
            <label>{brandName}</label>
          </div>
        ),
      };
    });
  }, [user_brands, filterValue]);

  // Campaign/Project options
  const projectOptions = useMemo(() => {
    const allProjects = projectsData?.pages?.flatMap(
      (page) => page?.data?.projects || []
    ) || [];

    if (!allProjects.length) {
      return [];
    }

    return allProjects.map((proj) => {
      return {
        value: proj.id,
        label: (
          <div className={optionsClassName}>
            <input
              type="checkbox"
              className="select-options form-check-input "
              checked={
                Array.isArray(filterValue) &&
                filterValue.some(
                  (filterVal) => Number(filterVal.value) === Number(proj.id),
                )
              }
            />
            <label>{proj.name}</label>
          </div>
        ),
      };
    });
  }, [projectsData, filterValue]);

  const filterVal =
    (Array.isArray(filterValue) &&
      filterValue.find(
        (option) => option.value === (filterValue || [])[0]?.value,
      )) ||
    {};

  // Helper to get formatted display label
  const getDisplayLabel = () => {
    if (!filterVal.value) return '';

    // For status - format "in_progress" to "In Progress"
    if (filterBy === 'state') {
      return String(filterVal.value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }

    // For other filters, extract text label
    if (typeof filterVal.label === 'string') {
      return filterVal.label;
    }

    // Extract from React element
    if (filterVal.label?.props?.children) {
      const children = filterVal.label.props.children;
      if (Array.isArray(children)) {
        for (const child of children) {
          if (child?.type === 'label' && child?.props?.children) {
            return child.props.children;
          }
        }
      }
    }

    return String(filterVal.value);
  };

  const displayLabel = getDisplayLabel();

  const transformedFilterValue = {
    ...filterVal,
    label: (
      <div className="d-flex justify-content-between ">
        {displayLabel}
        {Array.isArray(filterValue) && filterValue.length > 1 ? (
          <span
            className={
              'd-flex justify-content-center  align-items-center ' +
              Styles.Count
            }
          >
            +{filterValue.length - 1}
          </span>
        ) : (
          ''
        )}
      </div>
    ),
  };

  const onChangeSelect = ({ value, label }) => {
    if (
      Array.isArray(filterValue) &&
      filterValue.some((filterVal) => filterVal.value === value)
    ) {
      const trStatus =
        Array.isArray(filterValue) &&
        filterValue.filter((val) => val.value !== value);
      handleFilters(index, trStatus, 'filterValue');
    } else {
      handleFilters(
        index,
        [...(filterValue || []), { value, label }],
        'filterValue',
      );
    }
  };

  // Helper to extract clean text label from option
  const getCleanLabel = (option) => {
    // If label is already a string, use it
    if (typeof option.label === 'string') {
      return option.label;
    }

    // For content type and status options, extract from nested structure
    if (option.label?.props?.children) {
      const children = option.label.props.children;

      // Array of children - look for the label element
      if (Array.isArray(children)) {
        // Find label in array (usually second child after checkbox)
        for (const child of children) {
          if (child?.type === 'div' && child?.props?.children) {
            // For content type: <div><label>{text}</label></div>
            const nestedLabel = child.props.children;
            if (nestedLabel?.type === 'label' && nestedLabel?.props?.children) {
              return nestedLabel.props.children;
            }
          }
          // For status: Badge component with status prop
          if (child?.type?.name === 'Badge' && child?.props?.status) {
            return child.props.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        }
      }
    }

    // Fallback: try to extract from value
    if (option.value) {
      return String(option.value).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    return option.value || 'Unknown';
  };

  // Render simple option list for chip editing mode
  const renderChipOptions = (options) => {
    return (
      <div
        className={Styles.ChipOptionsContainer}
        style={{
          maxHeight: 'none',
          overflow: 'visible',
          overflowY: 'visible',
          overflowX: 'visible'
        }}
      >
        {options.map((option) => {
          const isChecked = Array.isArray(filterValue) &&
            filterValue.some((filterVal) => filterVal.value === option.value);

          const displayLabel = getCleanLabel(option);

          return (
            <div
              key={option.value}
              className={`${Styles.ChipOption} ${isChecked ? Styles.ChipOption__Selected : ''}`}
              onClick={() => onChangeSelect(option)}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => {}} // Handled by onClick above
                className="form-check-input"
              />
              <span className={Styles.ChipOption__Label}>
                {displayLabel}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const getFilterByComp = () => {
    if (!(filterBy && isOptions)) {
      return null;
    }

    const commonPropsSelect = {
      isSearchable: false,
      className: autoOpen ? 'filter-Select filter-Select--autoOpen' : 'filter-Select',
      classNamePrefix: 'filter-Select',
      styles: SelectStatusStyles,
      onChange: onChangeSelect,
      value: transformedFilterValue,
      closeMenuOnSelect: false,
      hideSelectedOptions: false,
      menuIsOpen: autoOpen ? true : undefined,
      defaultMenuIsOpen: autoOpen,
    };

    switch (filterBy) {
      case 'state':
        return chipMode ?
          renderChipOptions(getStatusFilterOptions(filterValue)) : (
          <Select
            {...commonPropsSelect}
            placeholder="Status"
            options={getStatusFilterOptions(filterValue)}
          />
        );
      case 'content_type':
        return chipMode ?
          renderChipOptions(getContentTypeFilterOptions(filterValue)) : (
          <Select
            {...commonPropsSelect}
            placeholder="Content type"
            options={getContentTypeFilterOptions(filterValue)}
          />
        );

      case 'deadline_date':
        return (
          <Datepicker
            className={' input form-control '}
            classNamePrefix="filter-plan-datepicker"
            wrapperClassName={Styles.filterDatePicker}
            dateFormat="dd/MM/yyyy"
            selected={filterValue}
            onChange={(newValue) =>
              handleFilters(index, newValue, 'filterValue')
            }
            placeholderText="Select date"
            showPopperArrow={false}
          />
        );

      case 'assignee_id':
        return chipMode ?
          renderChipOptions(userOptions) : (
          <Select
            {...commonPropsSelect}
            placeholder="Assigned To"
            options={userOptions}
          />
        );

      case 'brand_id':
        return chipMode ?
          renderChipOptions(brandOptions) : (
          <Select
            {...commonPropsSelect}
            placeholder="Brand"
            options={brandOptions}
          />
        );

      case 'project_id':
        return chipMode ?
          renderChipOptions(projectOptions) : (
          <Select
            {...commonPropsSelect}
            placeholder="Campaign"
            options={projectOptions}
          />
        );

      default:
        return null;
    }
  };

  return <>{getFilterByComp()}</>;
}

export default ContentFilter;
